import React, { useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import API from '../api';
import { useLang } from '../LangContext';

const LANGS = [{ code: 'en', label: 'EN' }, { code: 'am', label: 'አማ' }, { code: 'or', label: 'OR' }];

export default function BuyerRegister() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const redirectTo = searchParams.get('redirect') || '/buyer-dashboard';
  const { t, lang, switchLang } = useLang();

  const [form, setForm] = useState({
    full_name: '',
    phone: '',
    email: '',
    password: '',
    confirm_password: '',
  });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const set = (e) => {
    let { name, value } = e.target;
    if (name === 'phone') value = value.replace(/\D/g, '').slice(0, 9);
    setForm(f => ({ ...f, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!form.full_name.trim()) { setError('Full name is required.'); return; }
    if (!form.phone || form.phone.length !== 9) { setError('Phone must be 9 digits.'); return; }
    if (form.password.length < 6) { setError(t.passwordShort); return; }
    if (form.password !== form.confirm_password) { setError(t.passwordMismatch); return; }

    setLoading(true);
    try {
      const payload = {
        full_name: form.full_name.trim(),
        phone: '+251' + form.phone,
        password: form.password,
      };
      if (form.email.trim()) payload.email = form.email.trim();

      const { data } = await API.post('/buyers/register', payload);
      localStorage.setItem('buyerToken', data.token);
      localStorage.setItem('buyer', JSON.stringify(data.buyer));
      navigate(redirectTo);
    } catch (err) {
      setError(err.response?.data?.message || 'Registration failed.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '40px 24px', background: 'linear-gradient(to bottom, #0a0a0a, #171717)', backgroundAttachment: 'fixed' }}>
      <div style={{ width: '100%', maxWidth: 460 }}>
        {/* Language picker */}
        <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 16, gap: 4 }}>
          {LANGS.map(l => (
            <button key={l.code} onClick={() => switchLang(l.code)} style={{ padding: '5px 12px', borderRadius: 6, border: 'none', fontSize: 12, cursor: 'pointer', background: lang === l.code ? '#f97316' : '#262626', color: lang === l.code ? '#000' : '#ffffff', fontWeight: lang === l.code ? 700 : 400 }}>
              {l.label}
            </button>
          ))}
        </div>

        <div style={{ background: '#171717', border: '1px solid #262626', borderRadius: 20, padding: '40px 32px' }}>
          {/* Header */}
          <div style={{ textAlign: 'center', marginBottom: 32 }}>
            <div style={{ fontSize: 40, marginBottom: 10 }}>🛒</div>
            <h1 style={{ fontSize: 22, fontWeight: 900, color: '#fff', marginBottom: 6 }}>{t.buyerRegisterTitle}</h1>
            <p style={{ color: '#ffffff', fontSize: 15 }}>
              {redirectTo !== '/buyer-dashboard'
                ? 'Create a free account to complete your purchase'
                : 'Shop instantly — no approval needed'}
            </p>
          </div>

          <form onSubmit={handleSubmit}>
            {/* Full Name */}
            <div style={{ marginBottom: 16 }}>
              <label style={labelStyle}>Full Name *</label>
              <input
                type="text"
                name="full_name"
                value={form.full_name}
                onChange={set}
                placeholder="Your full name"
                required
                style={inputStyle}
              />
            </div>

            {/* Phone */}
            <div style={{ marginBottom: 16 }}>
              <label style={labelStyle}>Phone Number *</label>
              <div style={{ display: 'flex', alignItems: 'center', background: '#0a0a0a', border: '1px solid #333', borderRadius: 8, overflow: 'hidden' }}>
                <span style={{ padding: '11px 12px', color: '#f97316', fontWeight: 700, fontSize: 16, borderRight: '1px solid #333', whiteSpace: 'nowrap' }}>+251</span>
                <input
                  type="tel"
                  name="phone"
                  value={form.phone}
                  onChange={set}
                  maxLength={9}
                  placeholder="9XX XXX XXX"
                  style={{ flex: 1, padding: '11px 12px', background: 'transparent', border: 'none', color: '#fff', fontSize: 16, outline: 'none' }}
                />
              </div>
            </div>

            {/* Email (optional) */}
            <div style={{ marginBottom: 16 }}>
              <label style={labelStyle}>Email (optional)</label>
              <input
                type="email"
                name="email"
                value={form.email}
                onChange={set}
                placeholder="your@email.com"
                style={inputStyle}
              />
            </div>

            {/* Password */}
            <div style={{ marginBottom: 16 }}>
              <label style={labelStyle}>Password *</label>
              <input
                type="password"
                name="password"
                value={form.password}
                onChange={set}
                placeholder={t.minPassword}
                required
                style={inputStyle}
              />
            </div>

            {/* Confirm Password */}
            <div style={{ marginBottom: 24 }}>
              <label style={labelStyle}>Confirm Password *</label>
              <input
                type="password"
                name="confirm_password"
                value={form.confirm_password}
                onChange={set}
                placeholder="Repeat your password"
                required
                style={inputStyle}
              />
            </div>

            {/* Error */}
            {error && (
              <div style={{ background: '#ef444420', border: '1px solid #ef444440', color: '#ef4444', borderRadius: 8, padding: '10px 14px', fontSize: 15, marginBottom: 16 }}>
                {error}
              </div>
            )}

            {/* Submit */}
            <button
              type="submit"
              disabled={loading}
              style={{ width: '100%', padding: '13px', background: '#f97316', color: '#000', border: 'none', borderRadius: 10, fontWeight: 700, fontSize: 15, cursor: loading ? 'not-allowed' : 'pointer', opacity: loading ? 0.7 : 1 }}
            >
              {loading ? 'Creating account...' : 'Create Buyer Account →'}
            </button>
          </form>

          <div style={{ textAlign: 'center', marginTop: 20, fontSize: 15, color: '#ffffff' }}>
            {t.alreadyHave}{' '}
            <Link to="/login" style={{ color: '#f97316', textDecoration: 'none' }}>{t.signIn}</Link>
          </div>
          <div style={{ textAlign: 'center', marginTop: 8, fontSize: 15, color: '#ffffff' }}>
            Want to sell?{' '}
            <Link to="/register" style={{ color: '#f97316', textDecoration: 'none' }}>{t.registerAsSeller}</Link>
          </div>
        </div>
      </div>
    </div>
  );
}

const inputStyle = {
  width: '100%',
  padding: '11px 14px',
  background: '#0a0a0a',
  border: '1px solid #333',
  borderRadius: 8,
  color: '#fff',
  fontSize: 16,
  boxSizing: 'border-box',
};

const labelStyle = {
  display: 'block',
  fontSize: 15,
  color: '#ffffff',
  marginBottom: 6,
};
